import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertCircle, FiChevronDown, FiChevronUp, FiCalendar, FiTag, FiFileText } from 'react-icons/fi';
import { AuthContext } from '../../context/AuthContext';

const Message = () => {
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [contractData, setContractData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchExpiringContracts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/bulk/expiring-contracts`,
          {
            headers: {
              'x-auth-token': authState.token,
            },
          }
        );

        console.log('Expiring Contracts Data:', response.data);
        setContractData(response.data);
      } catch (error) {
        console.error('Error fetching expiring contracts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (authState.token) {
      fetchExpiringContracts();
    }
  }, [authState.token]);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const ContractGroup = ({ group, title }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="mt-4"
    >
      <h3 className="font-semibold text-lg mb-2 text-indigo-950 dark:text-slate-200">{title}</h3>
      <ul className="space-y-4">
        {group.contracts.map((contract, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 * index }}
            className="bg-gray-100 dark:bg-slate-700 p-4 rounded-md text-sm text-gray-700 dark:text-gray-300"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">{contract.contractNumber}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                contract.daysRemaining <= 30 ? 'bg-red-100 text-red-800' : 
                contract.daysRemaining <= 60 ? 'bg-yellow-100 text-yellow-800' : 
                'bg-green-100 text-green-800'
              }`}>
                {contract.daysRemaining} days left
              </span>
            </div>
            <p className="mb-2 flex items-center">
              <FiFileText className="mr-2" /> {contract.contractDescription}
            </p>
            <p className="mb-2 flex items-center">
              <FiTag className="mr-2" /> {contract.category}
            </p>
            <p className="flex items-center">
              <FiCalendar className="mr-2" /> Expires: {new Date(contract.endDate).toLocaleDateString()}
            </p>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col bg-white dark:bg-slate-800 gap-6 p-6 m-2 rounded-lg shadow-lg max-h-[calc(100vh-4rem)] overflow-hidden"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-xl text-indigo-950 dark:text-slate-200">Expiring Contracts</h2>
          <p className="text-sm font-medium text-indigo-500 dark:text-indigo-400">{new Date().toDateString()}</p>
        </div>
        <FiAlertCircle className="text-3xl text-yellow-500" />
      </div>
      <AnimatePresence>
        {loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex justify-center items-center h-20"
          >
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-start overflow-y-auto"
          >
            {contractData && contractData.reminderMessages && (
              <>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Hi {authState.user.firstname}, you have contracts expiring soon. Please review the details below.
                </p>
                <div className={`w-full ${expanded ? '' : 'max-h-96'} overflow-y-auto pr-2`}>
                  {contractData.reminderMessages.map((group, index) => (
                    <ContractGroup 
                      key={index} 
                      group={group} 
                      title={`Contracts expiring within ${group.daysRemaining} days`} 
                    />
                  ))}
                </div>
                {contractData.reminderMessages.reduce((acc, group) => acc + group.contracts.length, 0) > 5 && (
                  <button
                    className="flex items-center text-blue-500 font-bold text-sm hover:underline focus:outline-none mt-4"
                    onClick={toggleExpand}
                  >
                    {expanded ? (
                      <>
                        View less <FiChevronUp className="ml-1" />
                      </>
                    ) : (
                      <>
                        View more <FiChevronDown className="ml-1" />
                      </>
                    )}
                  </button>
                )}
              </>
            )}
            {(!contractData || !contractData.reminderMessages || contractData.reminderMessages.length === 0) && (
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Hi {authState.user.firstname}, there are no contracts expiring soon.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Message;